import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useToast } from '@/components/ui/use-toast';
import { ImageIcon, Save } from 'lucide-react';
import MultiImageUpload from '@/components/admin/MultiImageUpload';
import siteContentService, {
  type FounderImagesUpdate,
} from '@/services/siteContentService';
import { FOUNDER_IMAGES_QUERY_KEY } from '@/hooks/useFounderImages';

/**
 * Card "Fundadora": reemplaza las dos fotos de la sección Fundadora sin tocar
 * código ni redeploy (HU-09).
 *
 * Solo se envían las variantes que cambiaron: mandar las dos pisaría la que la
 * administradora no quiso tocar.
 */
const FundadoraCard = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<FounderImagesUpdate>({});

  const { data: founderImages } = useQuery({
    queryKey: FOUNDER_IMAGES_QUERY_KEY,
    queryFn: () => siteContentService.getFounderImages(),
    staleTime: 5 * 60 * 1000,
  });

  const saveMutation = useMutation({
    mutationFn: () => siteContentService.updateFounderImages(pending),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FOUNDER_IMAGES_QUERY_KEY });
      setPending({});
      toast({ title: 'Imágenes de la fundadora actualizadas' });
    },
    onError: () => {
      toast({
        title: 'No se pudieron guardar las imágenes',
        description: 'Revisá el formato y el peso del archivo e intentá de nuevo.',
        variant: 'destructive',
      });
    },
  });

  const hasChanges = Object.keys(pending).length > 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          Fundadora
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <label className="block text-sm font-medium mb-2">
            Retrato de "Nuestra historia" (vertical)
          </label>
          <MultiImageUpload
            values={
              pending.imageAbout
                ? [pending.imageAbout]
                : founderImages?.imageAbout
                  ? [founderImages.imageAbout]
                  : []
            }
            onChange={(values) => {
              const [url] = values;
              if (url) setPending((prev) => ({ ...prev, imageAbout: url }));
            }}
            maxImages={1}
            orientacion="vertical"
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Foto compacta de la home (circular)
          </label>
          <MultiImageUpload
            values={
              pending.imageCompact
                ? [pending.imageCompact]
                : founderImages?.imageCompact
                  ? [founderImages.imageCompact]
                  : []
            }
            onChange={(values) => {
              const [url] = values;
              if (url) setPending((prev) => ({ ...prev, imageCompact: url }));
            }}
            maxImages={1}
            orientacion="horizontal"
          />
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button
            onClick={() => saveMutation.mutate()}
            disabled={!hasChanges || saveMutation.isPending}
            className="flex items-center gap-2"
          >
            <Save className="h-4 w-4" />
            {saveMutation.isPending ? 'Guardando...' : 'Guardar'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FundadoraCard;
